import React from 'react';
import { FileText, ExternalLink, GraduationCap, MapPin, ClipboardList } from 'lucide-react';
import Footer from '../components/Footer';

const Enrollment = () => {
  const steps = [
    "Visit MySchools.nyc to create an account",
    "Browse and add schools to your application",
    "Submit your application during the enrollment period",
    "Check for waitlists and receive your offer",
    "Complete registration at your assigned school"
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="text-center mb-16">
          <span className="text-[#7d5800] font-bold text-sm uppercase tracking-wider">Join Us</span>
          <h1 className="text-5xl md:text-6xl font-bold text-[#123f1e] mt-4">
            Enrollment
          </h1>
          <p className="text-xl text-gray-600 mt-4 max-w-3xl mx-auto">
            Reach out with any questions about enrolling your child in our Pre-K programs
          </p>
        </header>

        {/* Welcome Image */}
        <div className="mb-16">
          <img
            src="/assets/images/gallery/District9PreK-173.jpg"
            alt="GoLD Pre-K students in a classroom learning environment"
            className="w-full max-w-5xl mx-auto h-96 object-cover rounded-3xl shadow-xl"
          />
        </div>

        {/* How to Apply Section */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#123f1e] mb-8 text-center tracking-tight">How to Apply</h2>
          <div className="bg-[#123f1e] rounded-3xl p-12 text-white">
            <div className="flex items-center gap-4 mb-8">
              <div className="p-4 bg-[#febc04] rounded-2xl">
                <FileText className="w-8 h-8 text-[#123f1e]" />
              </div>
              <h3 className="text-3xl font-bold">Apply Online</h3>
            </div>

            <p className="text-white/90 text-lg mb-8 leading-relaxed">
              During 3-K, pre-K, and kindergarten application periods, families can use MySchools to apply online, explore programs, manage waitlists, and get their offer.
            </p>

            <a
              href="https://www.myschools.nyc/en/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#febc04] text-[#123f1e] font-bold rounded-xl hover:bg-[#e5a903] transition-all shadow-md hover:shadow-lg"
            >
              <span>Visit MySchools.nyc</span>
              <ExternalLink className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* Enrollment Steps */}
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-[#123f1e] mb-8 text-center">Enrollment Process</h2>
          <div className="space-y-4">
            {steps.map((step, index) => (
              <div key={index} className="flex items-start gap-4 bg-white border-2 border-gray-200 rounded-2xl p-6 hover:border-[#febc04] transition-all">
                <div className="flex-shrink-0 w-10 h-10 bg-[#123f1e] rounded-full flex items-center justify-center text-white font-bold">
                  {index + 1}
                </div>
                <p className="text-gray-700 text-lg pt-1">{step}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Moving Up - Kindergarten & Nearby Schools */}
        <div className="mt-16 max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#123f1e] mb-8 text-center tracking-tight">Moving Up</h2>

          <div className="mb-8">
            <img
              src="/assets/images/gallery/District9PreK-177.jpg"
              alt="Students preparing for kindergarten"
              className="w-full h-64 object-cover rounded-2xl shadow-xl"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-16">
            {/* Kindergarten Walk Through */}
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-[#febc04] transition-all shadow-sm hover:shadow-md">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-4 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl shadow-md">
                  <GraduationCap className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#123f1e] tracking-tight">Kindergarten</h3>
              </div>
              <p className="text-gray-600 leading-relaxed mb-6">
                Learn about the transition from Pre-K to Kindergarten and find resources to help prepare your child for the next step.
              </p>
              <a
                href="https://www.schools.nyc.gov/enrollment/enroll-grade-by-grade/kindergarten"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#123f1e] font-semibold hover:text-[#7d5800] transition-colors"
              >
                <span>Kindergarten Walk Through</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            {/* Find Your Zoned School */}
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-[#febc04] transition-all shadow-sm hover:shadow-md">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-4 bg-gradient-to-br from-green-600 to-green-700 rounded-xl shadow-md">
                  <MapPin className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#123f1e] tracking-tight">Nearby Schools</h3>
              </div>
              <p className="text-gray-600 leading-relaxed mb-6">
                Search for elementary schools in your neighborhood and find your zoned school for kindergarten enrollment.
              </p>
              <a
                href="https://schoolsearch.schools.nyc/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#123f1e] font-semibold hover:text-[#7d5800] transition-colors"
              >
                <span>Find Your Zone School</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Waitlist Form */}
        <div className="mt-16 max-w-4xl mx-auto bg-[#123f1e] rounded-3xl p-12 text-white text-center shadow-xl">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#febc04]/20 rounded-full border-2 border-[#febc04]/30 mb-6 backdrop-blur-sm">
            <ClipboardList className="w-8 h-8 text-[#febc04]" />
          </div>
          <h2 className="text-3xl font-bold mb-4 tracking-tight">Join Our Waitlist</h2>
          <p className="text-white/90 text-lg mb-8 leading-relaxed max-w-2xl mx-auto">
            Interested in enrolling your child at GoLD but spots are full? Join our waitlist and we'll contact you when spaces become available.
          </p>
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLSdqpqYqusvTY9VIdVAGoBZVVVSr-PSPJhY937uPOTPFPsjbbg/viewform"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 px-8 py-4 bg-[#febc04] text-[#123f1e] font-bold rounded-xl hover:bg-[#ffd04f] transition-all shadow-md hover:shadow-lg hover:scale-105"
          >
            <span>Complete Waitlist Form</span>
            <ExternalLink className="w-5 h-5" />
          </a>
        </div>

        {/* Contact CTA */}
        <div className="mt-16 max-w-4xl mx-auto bg-[#febc04]/10 border-2 border-[#febc04] rounded-3xl p-12 text-center">
          <h2 className="text-2xl font-bold text-[#123f1e] mb-4">
            Need Help with Enrollment?
          </h2>
          <p className="text-gray-700 mb-6">
            Our staff is here to assist you through the enrollment process. Contact us with any questions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="px-8 py-4 bg-[#123f1e] text-white font-bold rounded-xl hover:bg-[#1a5a2d] transition-all"
            >
              Contact Us
            </a>
            <a
              href="tel:718-583-8975"
              className="px-8 py-4 border-2 border-[#123f1e] text-[#123f1e] font-bold rounded-xl hover:bg-[#123f1e] hover:text-white transition-all"
            >
              Call (718) 583-8975
            </a>
          </div>
        </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Enrollment;
